import type { Movie, MovieItem, Tag, Cat } from "@/types/movies";
import { slugify } from "@/lib/slugify";

type CardItem = Omit<MovieItem, "duration" | "tags"> &
  Pick<Movie, "persons" | "duration" | "tags">;

type AgeRestrictionLike =
  | string
  | { name?: string }
  | null
  | undefined;

interface MapperSource {
  uuid?: string;
  UUID?: string;
  unique_code?: string;
  UniqueCode?: string;
  title_fa?: string;
  title_en?: string;
  poster_url?: string | null;
  release_date?: string | null;
  duration?: string | null;
  imdb_rate?: number | string | null;
  rating?: number | null;
  type?: string | null;
  entity_type?: string;
  content_type?: string;
  content_access?: string | null;
  has_subtitles?: string | null;
  has_dubbing?: string | null;
  hasonlinescreening?: boolean | null;
  subtitles?: { uuid?: string }[];
  dubs?: { uuid?: string }[];
  tags: Tag[];
  category?: Cat[];
  categories?: Cat[];
  age_restriction?: AgeRestrictionLike;
  coming_soon?: boolean;
  status?: string;
  summary?: string;
  href?: string | null;
  medias?: { media_type?: string; media_url?: string }[];
}

const normalizeAgeRestriction = (v: AgeRestrictionLike): string | null => {
  if (!v) return null;
  if (typeof v === "string") return v;
  return v.name ?? null;
};

const buildHref = (src: MapperSource): string | undefined => {
  if (src.href) return src.href;
  const code = src.unique_code ?? src.UniqueCode;
  if (!code) return undefined;
  const kind = src.entity_type ?? src.type;
  const slug = slugify(src.title_en || src.title_fa);
  const suffix = slug ? `/${slug}` : "";
  return kind === "series" ? `/s/${code}${suffix}` : `/movie/${code}${suffix}`;
};

const pickPoster = (
  src: MapperSource,
  opts?: { blockSlug?: string; placeholder?: string },
): string | null | undefined => {
  if (opts?.blockSlug === "special") {
    const featured = src.medias?.find(
      (m) => m.media_type === "featured_poster",
    )?.media_url;
    if (featured) return featured;
  }
  return src.poster_url ?? opts?.placeholder ?? null;
};

const truthyOrNull = (cond: boolean): string | null => (cond ? "true" : null);

export interface ToMovieItemOptions {
  blockSlug?: string;
  posterPlaceholder?: string;
}

export function toMovieItem(
  src: MapperSource,
  opts?: ToMovieItemOptions,
): CardItem {
  const imdbRate = src.imdb_rate ?? src.rating ?? null;
  const badges = src?.tags.filter((t) => t.type === "برچسب فیلم");
  const hasonlinescreening = badges.some(
    (t) => t.name_en === "onlinescreening",
  );
 
  return {
    persons: [],
    uuid: src.uuid ?? src.UUID,
    unique_code: src.unique_code ?? src.UniqueCode,
    title_fa: src.title_fa || "—",
    title_en: src.title_en || "—",
    poster_url: pickPoster(src, {
      blockSlug: opts?.blockSlug,
      placeholder: opts?.posterPlaceholder,
    }),
    release_date: src.release_date ?? null,
    duration: src.duration ?? undefined,
    imdb_rate: imdbRate,
    type: src.type ?? src.entity_type ?? null,
    content_type: src.content_type ?? src.entity_type,
    content_access: src.content_access ?? undefined,
    hasonlinescreening: hasonlinescreening ?? null,
    has_subtitles: truthyOrNull(
      Boolean(src.has_subtitles) || (src.subtitles?.length ?? 0) > 0,
    ),
    has_dubbing: truthyOrNull(
      Boolean(src.has_dubbing) || (src.dubs?.length ?? 0) > 0,
    ),
    age_restriction: normalizeAgeRestriction(src.age_restriction),
    tags: src.tags ?? [],
    category: src.category ?? src.categories ?? undefined,
    coming_soon: src.coming_soon,
    status: src.status,
    summary: src.summary,
    href: buildHref(src),
  };
}

export function toMovieItems(
  list: MapperSource[],
  opts?: ToMovieItemOptions,
): CardItem[] {
  return list.map((m) => toMovieItem(m, opts));
}

export type { Movie, MovieItem };
